<template>
  <v-main v-if="!familiesLoader">
    <div class="title-background">
      <TitleHeader
        :title="$t('familiesList.title')"
        :subtitle="$t('familiesList.subtitle')"
      />
    </div>
    <v-container
      id="list-container"
      fluid
      class="sz-primary-background py-0 pt-7"
    >
      <div class="v-container pa-0 mb-12">
        <v-row no-gutters>
          <v-col
            v-if="$vuetify.display.smAndUp"
            cols="12"
            sm="3"
            class="pr-0 pr-sm-3"
          >
            <FiltersComponent class="border-md" />
          </v-col>
          <v-col
            cols="12"
            sm="9"
            class="pl-0 pl-sm-3 mt-0"
          >
            <v-row no-gutters>
              <v-col
                cols="12"
                class="mb-3"
              >
                <ReturnLink
                  :label="$t('familiesList.returnToMainPage')"
                  to-name="home"
                  :query="queryCurrentProvinceName"
                />
              </v-col>
              <v-col
                cols="12"
                class="mb-5"
              >
                <v-row
                  no-gutters
                  justify="space-between"
                >
                  <v-col cols="auto">
                    <h2 class="f-24 mb-0 font-weight-medium sz-primary-grey--text">
                      {{ bubbleName }}
                    </h2>
                  </v-col>
                  <v-col
                    v-if="$vuetify.display.xs"
                    cols="auto"
                  >
                    <FiltersComponent />
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                cols="12"
                class="ma-0"
              >
                <ActiveFilters />
              </v-col>
              <v-col cols="12">
                <v-expand-transition>
                  <InfoBox
                    v-if="isFilterAlert"
                    class="mb-5 font-weight-medium"
                  >
                    <template #default>
                      {{ $t('familiesList.noFamiliesFilter.labelBeginning') }}
                      <a
                        class="primary-link"
                        @click="resetFilter"
                      >{{ $t('familiesList.noFamiliesFilter.removeFilters') }}</a>
                      {{ $t('familiesList.noFamiliesFilter.labelEnding') }}
                    </template>
                  </InfoBox>
                </v-expand-transition>
              </v-col>
              <v-col
                v-if="familiesList?.length > 0"
                cols="12"
                class="mb-5"
              >
                <div
                  :class="{'opacity-filter': getFilterLoader}"
                >
                  <ListComponent
                    :items="familiesList"
                  />
                </div>
              </v-col>
              <v-expand-transition>
                <v-col
                  v-if="isBoxFilterVisible"
                  cols="12"
                  class="mb-5"
                >
                  <InfoBox
                    :title="$t('familiesList.infoFamiliesGreaterNeedsHighlight')"
                    :text="$t('familiesList.infoFamiliesGreaterNeeds')"
                    inline
                  >
                    <a
                      v-if="!isDifficult"
                      class="primary-link cursor-pointer f-16"
                      @click="setIsDifficult(true)"
                    >
                      {{ $t('familiesList.showFamiliesGreaterNeeds') }}
                    </a>
                    <a
                      v-else
                      class="primary-link cursor-pointer f-16"
                      @click="setIsDifficult(false)"
                    >
                      {{ $t('familiesList.showAllFamilies') }}
                    </a>
                  </InfoBox>
                </v-col>
              </v-expand-transition>
              <v-col
                v-if="!isFilterAlert"
                cols="12"
              >
                <FamiliesPagination :items-amount="familiesAmount" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <v-container class="py-0">
      <v-row no-gutters>
        <v-col cols="12">
          <BannerBox v-bind="$tm('banners.SzPHomeBannerProvinceChoosed')" />
        </v-col>
        <v-col cols="12">
          <FAQPanels :faq="FAMILIES_PAGE_FAQ" />
        </v-col>
        <v-col cols="12">
          <SponsorsComponent class="w-100 mt-2 mb-5" />
        </v-col>
      </v-row>
    </v-container>
    <FooterComponent />
    <ModalCustom v-model="isFamiliesListEmpty">
      <template #header>
        <p class="f-24 mb-2 font-weight-medium">
          {{ $t('familiesList.allFamiliesChosen') }}
        </p>
        <p class="f-18 mb-2">
          {{ $t('familiesList.chooseOtherLocation') }}
        </p>
        <p class="f-18 mb-0">
          {{ $t('familiesList.checkAgainLater') }}
        </p>
        <router-link
          :to="{ name: ROUTE_NAMES.HOME }"
          class="primary-button d-block d-md-inline-block pl-0 mt-5 mb-3 text-center"
        >
          {{ $t('familiesList.returnToMainPage') }}
        </router-link>
      </template>
      <template #footer>
        <p class="mb-0">
          {{ $t('family.donateForSzp') }}
        </p>
        <a
          id="swr-step1-payment-no-family"
          :href="szpPaymentFormModalZero"
          target="_blank"
          class="secondary-button d-inline-block pl-0 mt-5 mb-3"
        >
          {{ $t('label.donateForSzp') }}
        </a>
      </template>
    </ModalCustom>
    <ScrollTopButton />
  </v-main>
  <LoaderComponent
    v-else
    page
  />
</template>

<script>
import FamiliesPagination from '@/components/FamiliesList/FamiliesPagination';
import ActiveFilters from '@/components/FamiliesList/Filters/ActiveFilters';
import FiltersComponent from '@/components/FamiliesList/Filters/FiltersComponent';
import ListComponent from '@/components/FamiliesList/ListComponent';
import BannerBox from '@/components/widgets/BannerBox/BannerBox.vue';
import FAQPanels from '@/components/widgets/FAQPanels';
import FooterComponent from '@/components/widgets/FooterComponent';
import InfoBox from '@/components/widgets/InfoBox.vue';
import LoaderComponent from '@/components/widgets/LoaderComponent';
import ModalCustom from '@/components/widgets/ModalCustom';
import ReturnLink from '@/components/widgets/ReturnLink';
import ScrollTopButton from '@/components/widgets/ScrollTopButton';
import SponsorsComponent from '@/components/widgets/SponsorsComponent';
import TitleHeader from '@/components/widgets/TitleHeader';
import { LINKS } from '@/constants/externalLinks';
import { FAMILIES_PAGE_FAQ } from '@/constants/faq';
import { createEmptyFamiliesListEvent } from '@/plugins/gtm/events';
import { useGoTo } from 'vuetify';
import ROUTE_NAMES from '@/constants/routeNames';

export default {
    name: 'FamiliesList',
    components: {
        ScrollTopButton,
        ModalCustom,
        FAQPanels,
        SponsorsComponent,
        FooterComponent,
        ListComponent,
        FiltersComponent,
        ActiveFilters,
        FamiliesPagination,
        InfoBox,
        ReturnLink,
        TitleHeader,
        LoaderComponent,
        BannerBox
    },
    beforeRouteLeave (to, from, next) {
        this.$store.commit('families/setScrollSavedPosition', {
            x: window.scrollX,
            y: window.scrollY
        });
        this.$store.commit('families/clear');
        next();
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.$store.commit('families/clearBubbleName');
            if (from.name === ROUTE_NAMES.HOME || from.name === ROUTE_NAMES.HOME_SECURE || from.name == null) {
                vm.$store.dispatch('families/resetFilter');
                vm.$store.dispatch('families/resetPagination');
            }
            vm.$store.commit('families/setBubbleId', to.params.bubbleId);
            if (from.name !== ROUTE_NAMES.FAMILY) {
                vm.$store.commit('families/setScrollSavedPosition', {
                    x: 0,
                    y: 0
                });
            }
        });
    },
    props: {
        bubbleId: {
            required: true
        }
    },
    setup () {
        const goTo = useGoTo();
        return { goTo };
    },
    data () {
        return {
            FAMILIES_PAGE_FAQ,
            szpPaymentFormModalZero: LINKS.szpPaymentFormModalZero,
            isFamiliesListEmpty: false,
            ROUTE_NAMES
        };
    },
    computed: {
        bubbleName () {
            return this.$store.getters['families/getBubbleName'];
        },
        getFilter () {
            return this.$store.getters['families/getFilter'];
        },
        familiesList () {
            return this.$store.getters['families/getFamilies'];
        },
        familiesAmount () {
            return this.$store.getters['families/getFamiliesAmount'];
        },
        familiesLoader () {
            return this.$store.getters['families/getLoader'];
        },
        isDifficult () {
            return this.getFilter.isDifficult === 1;
        },
        isBoxFilterVisible () {
            return !(this.getFilter.categoryId >= 0 || this.getFilter.warehouseId >= 0);
        },
        isFilterNotSet () {
            return this.getFilter.categoryId === -1 && this.getFilter.warehouseId === -1 && !this.isDifficult;
        },
        isFilterAlert () {
            return this.familiesAmount === 0 && (!this.isBoxFilterVisible || this.isDifficult);
        },
        queryCurrentProvinceName () {
            const currentProvinceName = this.$store.getters['provinces/getCurrentProvinceName'];
            return currentProvinceName ? { province: currentProvinceName } : null;
        },
        getFilterLoader () {
            return this.$store.getters['families/getFilterLoader'];
        }
    },
    watch: {
        bubbleName: {
            handler (val) {
                this.isFamiliesListEmpty = this.familiesAmount === 0 && this.isFilterNotSet;
                if (this.isFamiliesListEmpty) {
                    this.$nextTick(() => {
                        const gtmEventValue = {
                            id: this.bubbleId,
                            name: val,
                            ga: {
                                provinceId: this.$route.params.provinceId,
                                provinceName: this.$route.params.provinceName
                            }
                        };
                        this.$gtm.trackEvent(createEmptyFamiliesListEvent(gtmEventValue));
                    });
                }
            },
            immediate: true
        },
        getFilter: {
            handler (val, prev) {
                // @Todo: przy przepisywaniu na capi można rozbić na oddzielne metody
                if (prev == null) {
                    this.$store.commit('families/setLoader', true);
                } else {
                    this.$store.commit('families/cancelGetFamilies');
                    this.$store.commit('families/setFilterLoader', true);
                    this.$nextTick(() => {
                        this.goTo('#list-container', {
                            easing: 'easeInQuad'
                        });
                    });
                }
                this.$store.dispatch('families/getFamilies', this.bubbleId);
            },
            immediate: true,
            deep: true
        },
        familiesLoader: {
            handler (val) {
                if (val === false) {
                    this.$nextTick(() => {
                        document.documentElement.scrollTop = this.$store.getters['families/getScrollSavedPosition'].y;
                        this.$store.commit('families/setScrollSavedPosition', {
                            x: 0,
                            y: 0
                        });
                    });
                }
            },
            immediate: true
        }
    },
    methods: {
        setIsDifficult (val) {
            this.$store.commit('families/setFilter', { isDifficult: val ? 1 : -1, offset: 1 });
        },
        resetFilter () {
            this.$store.dispatch('families/resetFilter');
        }
    }
};
</script>

<style scoped></style>
